import { appConfig } from '@/config/app.config';

/**
 * A type guard to check if an item is an object with an 'item' property of type string.
 */
function isItemObject(item: unknown): item is { item: string } {
  return (
    typeof item === 'object' &&
    item !== null &&
    'item' in item &&
    typeof (item as { item: string }).item === 'string'
  );
}

/**
 * Safely normalizes an unknown value from i18next into a string array.
 * It handles two primary structures:
 * 1. A simple array of strings (`string[]`).
 * 2. An array of objects, each with an 'item' property (`{ item: string }[]`).
 *
 * If the value matches one of these structures, it returns a normalized string array.
 * Otherwise, it returns an empty array to prevent runtime errors.
 *
 * @param value - The value to normalize, typically from t(key, { returnObjects: true }).
 * @returns A string array (string[]), guaranteed not to be undefined.
 */
export function normalizeItemsToArray(value: unknown): string[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  if (typeof value[0] === 'string') {
    return value.every((item) => typeof item === 'string') ? value : [];
  }

  if (isItemObject(value[0])) {
    return value.every(isItemObject) ? value.map((item) => item.item) : [];
  }

  return [];
}

export function buildPhraseApiLoadPath(lng: string): string {
  const { api } = appConfig.phrase;
  if (!api) return '';

  const url = new URL(`${api.baseUrl}/${api.projectId}/locales/${lng}/download`);
  url.searchParams.set('file_format', 'i18next');
  if (api.branch && api.branch !== 'main') {
    url.searchParams.set('branch', api.branch);
  }

  return url.toString();
}

export function getPhraseApiHeaders(): Record<string, string> {
  const { api } = appConfig.phrase;
  if (!api) return {};

  return { Authorization: `Bearer ${api.accessToken}` };
}
